import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';
import { PenTool, Search, HelpCircle, MessageSquare, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { helpCategories, helpArticles } from '@/data/helpContent';

function formatAnswer(text: string) {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    // Render bullet points
    if (line.startsWith('- ')) {
      const content = line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return (
        <li key={i} className="ml-4 list-disc text-muted-foreground" dangerouslySetInnerHTML={{ __html: content }} />
      );
    }
    // Render numbered list items
    if (/^\d+\./.test(line)) {
      const content = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return (
        <li key={i} className="ml-4 list-decimal text-muted-foreground" dangerouslySetInnerHTML={{ __html: content }} />
      );
    }
    // Blank line
    if (line.trim() === '') return <br key={i} />;
    // Normal paragraph with bold support
    const content = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    return (
      <p key={i} className="text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: content }} />
    );
  });
}

export default function Help() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let articles = helpArticles;

    if (activeCategory) {
      articles = articles.filter((a) => a.categoryId === activeCategory);
    }

    if (query.trim()) {
      const q = query.toLowerCase();
      articles = articles.filter(
        (a) =>
          a.question.toLowerCase().includes(q) ||
          a.answer.toLowerCase().includes(q) ||
          a.tags.some((t) => t.includes(q))
      );
    }

    return articles;
  }, [query, activeCategory]);

  // Group filtered articles by category for rendering
  const grouped = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    for (const article of filtered) {
      if (!map.has(article.categoryId)) map.set(article.categoryId, []);
      map.get(article.categoryId)!.push(article);
    }
    return map;
  }, [filtered]);

  const resultCount = filtered.length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title="Help Centre"
        description="Answers to common questions about WriteAssess, including getting started, plans, uploading work, grades, and data privacy."
        canonical="/help"
      />
      {/* Header */}
      <header className="border-b">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <PenTool className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl">WriteAssess</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Contact
            </Link>
            <Link to="/auth">
              <Button size="sm" variant="outline">Sign in</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-primary/5 to-background border-b">
        <div className="max-w-5xl mx-auto px-6 py-12 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
            <HelpCircle className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-3">Help Centre</h1>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Answers to common questions about WriteAssess — getting started, uploading writing, understanding grades, billing, and more.
          </p>

          {/* Search */}
          <div className="relative max-w-lg mx-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search the help centre…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 pr-10 h-11 text-base"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Category filter */}
      <div className="border-b bg-background sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto scrollbar-thin pb-3">
          <button
            onClick={() => setActiveCategory(null)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeCategory === null
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
            }`}
          >
            All topics
          </button>
          {helpCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeCategory === cat.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
              }`}
            >
              <span>{cat.emoji}</span>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 max-w-5xl mx-auto px-6 py-10 w-full">
        {query && (
          <p className="text-sm text-muted-foreground mb-6">
            {resultCount === 0
              ? 'No results found.'
              : `${resultCount} result${resultCount !== 1 ? 's' : ''} for "${query}"`}
          </p>
        )}

        {resultCount === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-2">No articles match your search.</p>
            <button
              onClick={() => { setQuery(''); setActiveCategory(null); }}
              className="text-sm text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="space-y-10">
            {helpCategories
              .filter((cat) => grouped.has(cat.id))
              .map((cat) => {
                const articles = grouped.get(cat.id)!;
                return (
                  <section key={cat.id}>
                    <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground mb-4">
                      <span className="text-xl">{cat.emoji}</span>
                      {cat.label}
                      <Badge variant="secondary" className="ml-1 text-xs font-normal">
                        {articles.length}
                      </Badge>
                    </h2>

                    <Accordion type="multiple" className="space-y-2">
                      {articles.map((article) => (
                        <AccordionItem
                          key={article.id}
                          value={article.id}
                          className="border rounded-lg px-4 bg-card"
                        >
                          <AccordionTrigger className="text-left text-sm font-medium hover:no-underline py-4">
                            {article.question}
                          </AccordionTrigger>
                          <AccordionContent className="pb-4">
                            <div className="space-y-2">
                              {formatAnswer(article.answer)}
                            </div>

                            {article.relatedLinks && article.relatedLinks.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                                <span className="text-xs text-muted-foreground self-center">Related:</span>
                                {article.relatedLinks.map((link) => (
                                  <Link
                                    key={link.href}
                                    to={link.href}
                                    className="text-xs text-primary hover:underline"
                                  >
                                    {link.label} →
                                  </Link>
                                ))}
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </section>
                );
              })}
          </div>
        )}

        {/* Still need help CTA */}
        <div className="mt-16 rounded-2xl border bg-primary/5 p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-4">
            <MessageSquare className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Still need help?</h3>
          <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">
            Can't find what you're looking for? Send us a message and we'll get back to you.
          </p>
          <Link to="/contact">
            <Button className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Contact support
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col items-center gap-5 text-center">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                <PenTool className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-bold">WriteAssess</span>
            </Link>
            <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/data-protection" className="hover:text-foreground transition-colors">Data Protection</Link>
              <Link to="/safeguarding" className="hover:text-foreground transition-colors">Safeguarding</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </nav>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} WriteAssess. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
